import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const SectionContainer = styled.section`
  padding: 120px 0;
  background: #474545;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;

  @media (max-width: 768px) {
    gap: 60px;
  }
`;

const StepRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StepContent = styled.div`
  padding: 20px;
  order: ${props => props.reverse ? 2 : 1};

  @media (max-width: 768px) {
    order: 1;
    padding: 0;
  }
`;

const StepVisualWrapper = styled.div`
  order: ${props => props.reverse ? 1 : 2};

  @media (max-width: 768px) {
    order: 2;
  }
`;

const StepNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  font-size: 1.4rem;
  font-weight: 700;
  color: #FAECEC;
  margin-bottom: 24px;
  box-shadow: 0 8px 25px rgba(0, 151, 178, 0.35);
`;

const StepTitle = styled.h3`
  font-size: 1.9rem;
  font-weight: 600;
  color: #FAECEC;
  margin-bottom: 16px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StepDescription = styled.p`
  color: #FAECEC;
  opacity: 0.8;
  line-height: 1.7;
  font-size: 1.05rem;
`;

/* ── Phone-style screenshot frame ── */
const PhoneFrame = styled(motion.div)`
  position: relative;
  border-radius: 28px;
  background: #1a1a1a;
  border: 2px solid rgba(0, 151, 178, 0.3);
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.4),
    0 30px 60px rgba(0,0,0,0.5),
    0 0 40px rgba(0, 151, 178, 0.12);
  overflow: hidden;
  transform-origin: center center;
  cursor: default;
  transition:
    box-shadow 0.4s ease,
    border-color 0.4s ease;

  &:hover {
    border-color: rgba(0, 151, 178, 0.6);
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.4),
      0 40px 70px rgba(0,0,0,0.5),
      0 0 60px rgba(0, 151, 178, 0.25);
  }
`;

const PhoneTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #111111;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.color};
  opacity: 0.8;
`;

const ScreenshotImg = styled.img`
  width: 100%;
  display: block;
  object-fit: cover;
`;

const GlowBehind = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  background: radial-gradient(ellipse, rgba(0, 151, 178, 0.18) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
  filter: blur(20px);
`;

const ScreenshotContainer = styled.div`
  position: relative;
`;

const steps = [
  {
    number: "1",
    title: "Sign Up & Connect Your Exchange",
    description: "Securely link one of crypto exchanges. Your API keys are encrypted and never used to withdraw funds.",
    screenshot: "https://customer-assets.emergentagent.com/job_628a8393-d775-410a-a2a2-f298aaa6ef5d/artifacts/8w90iazj_optimized-p1c70h04_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202026-02-20%20%D0%B2%206.45.38%E2%80%AFPM.webp",
    tilt: -2,
  },
  {
    number: "2",
    title: "Build Your Bot",
    description: "Use our AI Creator powered by LLMs (Claude, OpenAI, xAI, Gemini, DeepSeek) for instant setup, connect one of the ready-made f01i bots or create comprehensive AI custom trading strategies via Blocks.",
    screenshot: "https://customer-assets.emergentagent.com/job_628a8393-d775-410a-a2a2-f298aaa6ef5d/artifacts/qi1mna84_optimized-mk1accfb_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202026-02-20%20%D0%B2%206.47.44%E2%80%AFPM.webp",
    tilt: 2,
  },
  {
    number: "3",
    title: "Test & Optimize",
    description: "Run comprehensive simulations with historical data before going live. Refine your strategy risk-free.",
    screenshot: "https://customer-assets.emergentagent.com/job_628a8393-d775-410a-a2a2-f298aaa6ef5d/artifacts/7qint6fk_optimized-l7dz4ovt_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202026-02-20%20%D0%B2%207.01.11%E2%80%AFPM.webp",
    tilt: -2,
  },
  {
    number: "4",
    title: "Invest & Track",
    description: "Deploy your bot, monitor performance and enjoy fully automated trading.",
    screenshot: "https://customer-assets.emergentagent.com/job_628a8393-d775-410a-a2a2-f298aaa6ef5d/artifacts/nwrq4038_optimized-yln4sr97_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202026-02-20%20%D0%B2%206.59.11%E2%80%AFPM.webp",
    tilt: 2,
  },
];

const contentVariants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const screenshotVariants = (tilt) => ({
  hidden: { opacity: 0, x: 40, rotate: 0 },
  visible: {
    opacity: 1,
    x: 0,
    rotate: tilt,
    transition: { duration: 0.8, ease: 'easeOut' },
  },
});

const HowItWorksSection = () => (
  <SectionContainer id="how-it-works">
    <Container>
      <SectionHeader>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          How It Works
        </SectionTitle>
      </SectionHeader>

      <StepsContainer>
        {steps.map((step, index) => {
          const isEven = index % 2 !== 0;
          return (
            <StepRow
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
            >
              <StepContent
                as={motion.div}
                variants={isEven ? { hidden: { opacity: 0, x: 40 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } } } : contentVariants}
                reverse={isEven}
              >
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>

              <StepVisualWrapper reverse={isEven}>
                <ScreenshotContainer>
                  <GlowBehind />
                  <PhoneFrame
                    as={motion.div}
                    variants={screenshotVariants(step.tilt)}
                    whileHover={{ rotate: 0, scale: 1.03, transition: { duration: 0.3 } }}
                  >
                    <PhoneTopBar>
                      <Dot color="#ff5f56" />
                      <Dot color="#ffbd2e" />
                      <Dot color="#27c93f" />
                    </PhoneTopBar>
                    <ScreenshotImg
                      src={step.screenshot}
                      alt={step.title}
                      loading="lazy"
                    />
                  </PhoneFrame>
                </ScreenshotContainer>
              </StepVisualWrapper>
            </StepRow>
          );
        })}
      </StepsContainer>
    </Container>
  </SectionContainer>
);

export default HowItWorksSection;
